import { CustomPage as CustomPageState } from "../../../publicSite/publicSite.module"
import { newsletterPageWidget } from "./newsletter.widget"
import { buildValidator, SingleValidatorResult, singleValidatorResultBuilders, Validator, validatorBuilders, ValidatorResult, validatorResultBuilders } from "../../../../../Shared/Frontend/validators/validator"
import { t } from "i18next"
import { AsyncState, loadingAsyncState, unloadedAsyncState, Updater } from "widgets-for-react"
import { Entity } from "ts-lenses"
import { submitForm } from "./newsletter.api"
import { ImageSizesProps, ImageSourcesProps } from "../../modernBlocks/modernBlocks.state"

export type NewsletterPageState = {
  form: Partial<Form>
  validatorData: Partial<Form>
  validator: Validator<ValidatorForm, ValidatorForm>
  validationResults: ValidatorResult

  recaptcha: string

  submitResult: AsyncState<{}>
} & CustomPageState

type Form = {
  gender: "man" | "vrouw" | "anders" | ""
  initials: string
  insertion: string
  lastName: string
  email: string
  acceptTerms: boolean
}

export const newsletterPageState = (email?: string): NewsletterPageState => ({
  widget: newsletterPageWidget,
  form: {
    initials: "",
    insertion: "",
    lastName: "",
    email: email || "",
    acceptTerms: false,
    gender: "",
  },
  validatorData: {},
  validator: validators,
  validationResults: validatorResultBuilders.validating(""),
  recaptcha: "",
  submitResult: unloadedAsyncState(),
})


type ValidatorForm = Partial<
  Omit<Form, "gender"> & {
    gender: string
  }
>

export const possibleSalutations = ['man', 'vrouw', 'anders'] as const

export const validators = buildValidator<ValidatorForm>({
  initials: [b => b.notEmpty(), validatorBuilders.string("initials", () => t("Newsletter:Error.initials"))],
  // insertion: [b => b.notEmpty(), validatorBuilders.string("insertion", () => t("Newsletter:Error.firstName"))],
  lastName: [b => b.notEmpty(), validatorBuilders.string("lastName", () => t("Newsletter:Error.lastName"))],
  gender: [b => b.notEmpty(), validatorBuilders.string("gender", () => t("Newsletter:Error.gender"))],
  acceptTerms: [b => b.isTrue(), validatorBuilders.boolean("acceptTerms", () => t("Newsletter:Error.acceptTerms"))],
  email: [b => b.isEmail(), validatorBuilders.string("email", () => t("Newsletter:Error.email"))],
})

export type FormUpdater = (field: keyof Form) => void

export type SetState = (u: Updater<NewsletterPageState>) => void
export const updaters = {
  SetValidate: (field: keyof Form, s0: NewsletterPageState) => {
    let newValidatorData = {
      ...s0.validatorData,
      [field]: s0.form[field]
    }

    return {
      ...s0,
      validatorData: newValidatorData,
      validator: s0.validator.setValueToValidate(s0.form, newValidatorData),
    }
  },
  UpdateForm: (f: keyof Form, v: Partial<Form>[keyof Form], setState: SetState, validate?: boolean) => {
    setState(os => {
      let entity = Entity(os)
      entity = entity.setIn("form", od => od.set(f, _ => v)) as typeof entity
      if (validate) {
        const validator = updaters.SetValidate(f, entity.commit())
        entity = entity.set("validator", _ => validator.validator)
        entity = entity.set("validatorData", _ => validator.validatorData)
      }

      return entity.commit() as NewsletterPageState
    })
  },
  ValidateForm: (field: keyof Form, setState: SetState) => {
    setState(s0 => updaters.SetValidate(field, s0))
  },
  SubmitForm: (setState: SetState, r: string) => {
    setState(s0 => {
      if (!s0.validationResults.values.every(v => v.kind == "passed")) {
        let s1 = s0.validator.setValueToValidate(s0.form, s0.form)
        return {
          ...s0,
          validatorData: s0.form,
          validator: s1,
        }
      }

      return Entity(s0)
        .set("submitResult", _ => loadingAsyncState(() => submitForm(s0, r)))
        .commit()
    })
  },
}

export const getValidationMessage = (state: NewsletterPageState, field: keyof ValidatorForm): string | undefined => {
  const validation = state.form[field] !== undefined ? state.validationResults.path(field) : singleValidatorResultBuilders.validating(field)
  if (validation.kind === "failed") return validation.message
  return undefined
}
